// Phase 13 · Step 1: the proposal and agreement emails, shared by the dashboard (copy it yourself) and the
// portal function (Send to couple), so both build the same text. Pure: no Deno, no browser.
import { signersOf, signerEmails, ccOf, isEmail } from "./signers.js";

const fmt = n => "$" + Math.round(n).toLocaleString("en-US");
const dedupe = list => [...new Map(list.filter(isEmail).map(e => [e.trim().toLowerCase(), e.trim()])).values()];

// The proposal and agreement go to the couple and to whoever signs (Phase 12: a parent can be the signer),
// with anyone the couple asked to have copied.
export const partnerEmails = client => dedupe([...((client && client.partners) || []).map(p => p && p.email), ...signersOf(client).map(s => s.email)]).join(", ");
export const ccLine = client => { const to = new Set(partnerEmails(client).toLowerCase().split(", ").filter(Boolean)); return ccOf(client).filter(e => !to.has(e.trim().toLowerCase())).join(", "); };

/** Who a proposal or agreement sent from the app goes to: { to: [...], cc: [...] }, built from the stored client only. */
export function proposalRecipients(client, contract) {
  const to = dedupe([...((client && client.partners) || []).map(p => p && p.email), ...signerEmails(client, contract)]);
  const seen = new Set(to.map(e => e.toLowerCase()));
  return { to, cc: ccOf(client).filter(e => !seen.has(e.toLowerCase())) };
}

export const quoteInvestment = quote => quote.finalNumber ? fmt(quote.finalNumber) : (fmt(quote.rangeLow) + " – " + fmt(quote.rangeHigh));

/** The proposal email's body for the branded layout: no greeting or sign-off (the layout and the server add those). */
export function quoteEmailParagraphs(client, quote, settings) {
  const P = ["Thank you for telling me about your wedding. Here's my proposal for planning it with you.",
    "Package: " + quote.packageTier + ". Planning investment: " + quoteInvestment(quote) + "."];
  if (quote.showBreakdownToClient && quote.lineItems && quote.lineItems.length) {
    P.push("What's included: " + quote.lineItems.map(li => li.label + " (" + fmt(li.amount) + (li.note ? ", " + li.note : "") + ")").join(" · ") + ".");
  }
  if (settings && settings.paymentTerms) P.push("Payment terms: " + settings.paymentTerms);
  P.push("You can read it and approve it, or ask for changes, in your planning portal.");
  P.push("If you'd like to talk it through first, reply here and I'll find a time.");
  return P;
}

/** The agreement email's body for the branded layout. The full text is read and signed in the portal. */
export function contractEmailParagraphs() {
  return ["Here's our agreement. Read it at your own pace; when it feels right, sign it in your planning portal, or ask me for changes there."];
}

export const signOff = settings => {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  return "Warmly, " + (planner ? planner + " · " + biz : biz);
};
export const quoteSubject = settings => "Your " + ((settings && settings.businessName) || "GhostWriter Events") + " wedding planning proposal";
export const contractSubject = settings => "Your " + ((settings && settings.businessName) || "GhostWriter Events") + " coordination agreement";

// The copy-it-yourself versions (unchanged text from before Phase 13).
export function buildContractEmail(client, contract, settings, portalUrl) {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  const to = partnerEmails(client);
  const L = [];
  L.push("Hi " + client.names + ",");
  L.push("");
  if (portalUrl) {
    L.push("Here's the agreement. Read it at your own pace; when it feels right, sign it in your planning portal (or ask for changes there):");
    L.push("");
    L.push(portalUrl);
  } else {
    L.push("Here's the agreement, below. Read it at your own pace and reply with any questions; when it feels right, I'll arrange signatures.");
  }
  L.push("");
  L.push("——————————");
  L.push("");
  L.push(contract.body || "");
  L.push("");
  L.push("——————————");
  L.push("");
  L.push("Warmly,");
  L.push(planner ? (planner + " · " + biz) : biz);
  return { to: to, cc: ccLine(client), subject: contractSubject(settings), body: L.join("\n") };
}

export function buildQuoteEmail(client, quote, settings, portalUrl) {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  const to = partnerEmails(client);
  const L = [];
  L.push("Hi " + client.names + ",");
  L.push("");
  L.push("Thank you for telling me about your wedding. Here's my proposal for planning it with you:");
  L.push("");
  L.push("Package: " + quote.packageTier);
  L.push("Planning investment: " + quoteInvestment(quote));
  if (quote.showBreakdownToClient && quote.lineItems && quote.lineItems.length) {
    L.push("");
    L.push("What's included:");
    quote.lineItems.forEach(li => L.push("  • " + li.label + " — " + fmt(li.amount) + (li.note ? " (" + li.note + ")" : "")));
  }
  if (settings && settings.paymentTerms) { L.push(""); L.push("Payment terms: " + settings.paymentTerms); }
  L.push("");
  if (portalUrl) {
    L.push("You can read it and approve it, or ask for changes, in your planning portal:");
    L.push(portalUrl);
    L.push("");
    L.push("If you'd like to talk it through first, reply here and I'll find a time.");
  } else {
    L.push("If you'd like to talk it through, reply here and I'll find a time.");
  }
  L.push("");
  L.push("Warmly,");
  L.push(planner ? (planner + " · " + biz) : biz);
  return { to: to, cc: ccLine(client), subject: quoteSubject(settings), body: L.join("\n") };
}
